/**
 * 서버의 검사표를 불러와 고치고, 고친 것을 저절로 저장한다.
 *
 * 검사표는 반나절 걸려 채우는 문서라 [저장] 단추를 두지 않는다 — 누르는 것을 잊은 채 창을
 * 닫으면 판정이 통째로 사라진다. 판정 하나, 비고 한 줄을 고칠 때마다 잠깐 기다렸다 저장한다.
 *
 * **여러 사람이 같은 표를 본다.** 그래서 두 가지를 더 한다.
 * - 저장할 때 읽었던 판번호를 함께 보낸다. 그 사이 누가 먼저 고쳤으면 서버가 409 로 돌려보내고,
 *   어느 쪽을 남길지는 사람이 고른다(`takeServer` · `keepMine`). 조용히 덮어쓰지 않는다.
 * - 내가 고치는 중이 아닐 때는 30초마다 다시 읽어, 다른 사람이 채운 판정이 내 화면에도 뜬다.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { ApiCallError, type InspectionRecord, type SignageOneClient } from '../api/client.js';
import { describeError } from '../hooks.js';
import { clearLegacyDraft, readLegacyDraft, type ReportState } from './report.js';
import { hasContent, inputFromState, stateFromRecord } from './sync.js';

/** 마지막으로 고친 뒤 이만큼 조용하면 저장한다. 글자를 칠 때마다 저장하지 않게. */
const SAVE_DELAY_MS = 800;
/** 다른 사람이 고친 것을 다시 읽는 주기. */
const POLL_MS = 30_000;

export type SaveStatus =
  | { kind: 'loading' }
  | { kind: 'load-failed'; message: string }
  /** 서버와 같다. `at`·`by` 는 서버에 마지막으로 저장된 때와 사람(아직 없으면 null). */
  | { kind: 'saved'; at: string | null; by: string }
  | { kind: 'pending' }
  | { kind: 'saving' }
  | { kind: 'failed'; message: string }
  /** 읽은 뒤 누가 먼저 고쳤다. `current` 는 지금 서버에 있는 표다. */
  | { kind: 'conflict'; current: InspectionRecord };

export interface InspectionDraft {
  /** 불러오기 전에는 null. */
  state: ReportState | null;
  status: SaveStatus;
  update: (change: (prev: ReportState) => ReportState) => void;
  /** 불러오기에 실패했으면 다시 불러오고, 저장에 실패했으면 지금 다시 저장한다. */
  retry: () => void;
  /** 충돌 — 내 변경을 버리고 서버의 표를 받는다. */
  takeServer: () => void;
  /** 충돌 — 서버의 표 위에 내 것을 덮어쓴다. */
  keepMine: () => void;
}

function isRecord(value: unknown): value is InspectionRecord {
  return typeof value === 'object' && value !== null && typeof (value as { revision?: unknown }).revision === 'number';
}

export function useInspectionDraft(client: SignageOneClient, today: string): InspectionDraft {
  const [state, setState] = useState<ReportState | null>(null);
  const [status, setStatus] = useState<SaveStatus>({ kind: 'loading' });
  /** 저장이 끝날 때마다 올린다 — 저장하는 동안 들어온 변경을 이어서 저장하게 하는 방아쇠. */
  const [kick, setKick] = useState(0);

  const latest = useRef<ReportState | null>(null);
  const revision = useRef(0);
  /** 고친 횟수와 그중 서버에 올라간 횟수. 둘이 다르면 저장할 것이 남았다. */
  const edits = useRef(0);
  const savedEdits = useRef(0);
  const inFlight = useRef(false);
  const conflicted = useRef(false);
  /** 브라우저에만 있던 옛 초안을 올리는 중 — 올리고 나면 지운다. */
  const migrating = useRef(false);
  const alive = useRef(true);

  const adopt = useCallback(
    (record: InspectionRecord): void => {
      const next = stateFromRecord(record, today);
      revision.current = record.revision;
      latest.current = next;
      savedEdits.current = edits.current;
      conflicted.current = false;
      setState(next);
      setStatus({ kind: 'saved', at: record.updated_at, by: record.updated_by });
    },
    [today],
  );

  const load = useCallback(async (): Promise<void> => {
    setStatus({ kind: 'loading' });
    try {
      const record = await client.getInspection();
      if (!alive.current) return;
      if (record.revision === 0) {
        const legacy = readLegacyDraft(today);
        if (hasContent(legacy)) {
          // 서버가 비어 있고 이 브라우저에 예전 초안이 있다 — 그것을 서버로 옮긴다.
          revision.current = 0;
          latest.current = legacy;
          edits.current += 1;
          migrating.current = true;
          setState(legacy);
          setStatus({ kind: 'pending' });
          return;
        }
      } else {
        // 서버에 표가 있으면 옛 초안은 쓸 일이 없다.
        clearLegacyDraft();
      }
      adopt(record);
    } catch (cause) {
      if (alive.current) setStatus({ kind: 'load-failed', message: describeError(cause) });
    }
  }, [adopt, client, today]);

  const save = useCallback(async (): Promise<void> => {
    const current = latest.current;
    if (!current || inFlight.current || conflicted.current || edits.current === savedEdits.current) return;

    inFlight.current = true;
    const target = edits.current;
    setStatus({ kind: 'saving' });
    try {
      const record = await client.saveInspection(inputFromState(current, revision.current));
      revision.current = record.revision;
      savedEdits.current = target;
      if (migrating.current) {
        clearLegacyDraft();
        migrating.current = false;
      }
      if (!alive.current) return;
      setStatus(
        edits.current === target ? { kind: 'saved', at: record.updated_at, by: record.updated_by } : { kind: 'pending' },
      );
    } catch (cause) {
      if (!alive.current) return;
      if (cause instanceof ApiCallError && cause.status === 409 && isRecord(cause.details)) {
        conflicted.current = true;
        setStatus({ kind: 'conflict', current: cause.details });
      } else {
        setStatus({ kind: 'failed', message: describeError(cause) });
      }
    } finally {
      inFlight.current = false;
      if (alive.current) setKick((value) => value + 1);
    }
  }, [client]);

  useEffect(() => {
    alive.current = true;
    void load();
    return () => {
      alive.current = false;
    };
  }, [load]);

  // 고친 뒤 잠깐 조용하면 저장한다. 실패한 뒤에는 저절로 되풀이하지 않는다 — 사람이 [다시 시도] 한다.
  useEffect(() => {
    if (!latest.current || conflicted.current || edits.current === savedEdits.current) return;
    if (status.kind === 'failed' || status.kind === 'saving') return;
    const handle = window.setTimeout(() => void save(), SAVE_DELAY_MS);
    return () => window.clearTimeout(handle);
  }, [state, kick, status.kind, save]);

  // 내가 고치는 중이 아니면 다른 사람이 고친 것을 받아 온다.
  useEffect(() => {
    const handle = window.setInterval(() => {
      if (!latest.current || inFlight.current || conflicted.current || edits.current !== savedEdits.current) return;
      client
        .getInspection()
        .then((record) => {
          if (!alive.current || record.revision === revision.current) return;
          if (inFlight.current || edits.current !== savedEdits.current) return;
          adopt(record);
        })
        .catch(() => {
          // 다음 주기에 다시 읽는다. 읽기 실패로 편집을 막지 않는다.
        });
    }, POLL_MS);
    return () => window.clearInterval(handle);
  }, [adopt, client]);

  // 저장하지 못한 것이 남았는데 창을 닫으면 브라우저가 한 번 묻게 한다.
  useEffect(() => {
    const warn = (event: BeforeUnloadEvent): void => {
      if (edits.current === savedEdits.current) return;
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, []);

  const update = useCallback((change: (prev: ReportState) => ReportState): void => {
    if (!latest.current) return;
    const next = change(latest.current);
    latest.current = next;
    edits.current += 1;
    setState(next);
    if (!conflicted.current) setStatus((prev) => (prev.kind === 'saving' ? prev : { kind: 'pending' }));
  }, []);

  const retry = useCallback((): void => {
    if (!latest.current) {
      void load();
      return;
    }
    void save();
  }, [load, save]);

  const takeServer = useCallback((): void => {
    if (status.kind === 'conflict') adopt(status.current);
  }, [adopt, status]);

  const keepMine = useCallback((): void => {
    if (status.kind !== 'conflict') return;
    revision.current = status.current.revision;
    conflicted.current = false;
    // 서버와 같은 내용이었더라도 한 번은 올려 판번호와 저장한 사람을 내 것으로 맞춘다.
    edits.current += 1;
    setStatus({ kind: 'pending' });
    setKick((value) => value + 1);
  }, [status]);

  return { state, status, update, retry, takeServer, keepMine };
}
